/**
 * Procedural placeholder art for templates: faction-tinted SVG portraits, concept sheets and a
 * synthesized sound effect. Deterministic (seeded) so the sample project looks the same everywhere,
 * and generated at runtime so the repo ships no binary assets.
 */

export interface GeneratedFile {
  name: string;
  mime: string;
  bytes: Uint8Array;
  width?: number;
  height?: number;
  duration?: number;
  peaks?: number[];
}

export function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Part =
  | { d: string; mode: 'fill' | 'stroke'; tone?: 'fg' | 'bg' | 'accent'; width?: number }
  | { circle: [number, number, number]; mode: 'fill' | 'stroke'; tone?: 'fg' | 'bg' | 'accent'; width?: number };

/** Glyphs drawn on a 24×24 grid (like icons), scaled up ×10 in the portrait. */
export const GLYPHS = {
  flame: [
    { d: 'M12 2.5c.9 2.9 3.2 4.6 4.6 6.9 1 1.6 1.4 3.2 1.4 4.6a6 6 0 0 1-12 0c0-2.2 1-4 2.5-5.4.2 1.9 1 3.1 2.3 3.6-.5-3.3.1-6.6 1.2-9.7z', mode: 'fill' },
    { d: 'M12 12.6c.9 1.3 2.3 2.2 2.3 4a2.3 2.3 0 0 1-4.6 0c0-1.4.9-2.6 2.3-4z', mode: 'fill', tone: 'bg' },
  ],
  snow: [
    {
      d: 'M12 2v20M3.3 7l17.4 10M3.3 17l17.4-10M9.4 3.6 12 6.2l2.6-2.6M9.4 20.4 12 17.8l2.6 2.6M3.9 10.6l3.5-1-.9-3.5M20.1 13.4l-3.5 1 .9 3.5M3.9 13.4l3.5 1-.9 3.5M20.1 10.6l-3.5-1 .9-3.5',
      mode: 'stroke',
      width: 1.5,
    },
  ],
  leaf: [
    { d: 'M19.5 4.5C11 4.5 5.5 8.8 5.5 15c0 1.4.3 2.6.9 3.6L4.5 20.5l1 1 1.9-1.9c1 .6 2.2.9 3.6.9 6.2 0 8.5-7.6 8.5-16z', mode: 'fill' },
    { d: 'M8.4 17.6c3-4.5 6-7.5 9.3-10', mode: 'stroke', tone: 'bg', width: 1.1 },
  ],
  shield: [
    { d: 'M12 2.5l7.5 2.8v5.9c0 4.9-3.2 9-7.5 10.3-4.3-1.3-7.5-5.4-7.5-10.3V5.3z', mode: 'fill' },
    { d: 'M12 7v9M8.6 10.6h6.8', mode: 'stroke', tone: 'bg', width: 1.4 },
  ],
  bow: [{ d: 'M7 3c5.5 3 5.5 15 0 18M7 3v18M4.5 12H19M16.5 9.5 19 12l-2.5 2.5', mode: 'stroke', width: 1.5 }],
  crown: [
    { d: 'M3.5 17h17l-1.3-9.2-4.4 3.6L12 5l-2.8 6.4-4.4-3.6z', mode: 'fill' },
    { d: 'M4 18.8h16v2H4z', mode: 'fill' },
  ],
  golem: [
    { d: 'M8 3.5h8l4.5 5.5-1.8 9.5H5.3L3.5 9z', mode: 'fill' },
    { d: 'M9 7.5l2 3-1.5 3M15.5 8l-2 2.5 1 3.5M12 14.5v4', mode: 'stroke', tone: 'bg', width: 1.1 },
  ],
  tree: [{ d: 'M12 2l6.5 8.5h-3.2l4.2 6h-5.5v5h-4v-5H4.5l4.2-6H5.5z', mode: 'fill' }],
  staff: [
    { d: 'M5 21 16 8', mode: 'stroke', width: 1.8 },
    { circle: [17.6, 5.6, 3.1], mode: 'fill', tone: 'accent' },
  ],
  sword: [
    { d: 'M20 3.5l.5 2.8L10 16.8 7.2 14z', mode: 'fill' },
    { d: 'M5.4 12.4l6.2 6.2M7.6 16.4 3.6 20.4', mode: 'stroke', width: 1.8 },
  ],
  eye: [
    { d: 'M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z', mode: 'stroke', width: 1.5 },
    { circle: [12, 12, 2.9], mode: 'fill', tone: 'accent' },
  ],
  wave: [{ d: 'M2 9c2.5-2 4.5-2 7 0s4.5 2 7 0 4.5-2 6 0M2 15c2.5-2 4.5-2 7 0s4.5 2 7 0 4.5-2 6 0', mode: 'stroke', width: 1.6 }],
} satisfies Record<string, Part[]>;

export type GlyphName = keyof typeof GLYPHS;

export interface Palette {
  light: string;
  mid: string;
  dark: string;
  accent: string;
  particle: 'ember' | 'snow' | 'spore';
}

export const FACTION_PALETTES = {
  ember: { light: '#FFB067', mid: '#C2410C', dark: '#2A0A04', accent: '#FFE2A8', particle: 'ember' },
  frost: { light: '#B9E6FF', mid: '#2563A8', dark: '#07162E', accent: '#EAF8FF', particle: 'snow' },
  verdant: { light: '#B7F0A0', mid: '#2F7D45', dark: '#08200F', accent: '#F0FFE4', particle: 'spore' },
  arcane: { light: '#E3C8FF', mid: '#6D3BC4', dark: '#150A2E', accent: '#F6EDFF', particle: 'spore' },
} satisfies Record<string, Palette>;

const enc = (s: string) => new TextEncoder().encode(s);

export function portraitSvg(glyph: GlyphName, palette: Palette, seed = 1, pips = 0): string {
  const rnd = mulberry32(seed);
  const tone = (t: Part['tone']) => (t === 'bg' ? palette.mid : t === 'accent' ? palette.accent : '#FFFFFF');
  const parts = (GLYPHS[glyph] as Part[])
    .map((p) => {
      const color = tone(p.tone);
      const paint =
        p.mode === 'fill'
          ? `fill="${color}"`
          : `fill="none" stroke="${color}" stroke-width="${p.width ?? 1.5}" stroke-linecap="round" stroke-linejoin="round"`;
      return 'circle' in p ? `<circle cx="${p.circle[0]}" cy="${p.circle[1]}" r="${p.circle[2]}" ${paint}/>` : `<path d="${p.d}" ${paint}/>`;
    })
    .join('');
  let ticks = '';
  for (let i = 0; i < 36; i++) {
    const a = (i / 36) * Math.PI * 2;
    const r1 = i % 3 === 0 ? 184 : 190;
    const x1 = 256 + Math.cos(a) * r1;
    const y1 = 236 + Math.sin(a) * r1;
    const x2 = 256 + Math.cos(a) * 196;
    const y2 = 236 + Math.sin(a) * 196;
    ticks += `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}"/>`;
  }
  let particles = '';
  for (let i = 0; i < 26; i++) {
    const x = rnd() * 512;
    const y = palette.particle === 'ember' ? 180 + rnd() * 330 : rnd() * 512;
    const r = palette.particle === 'snow' ? 1.5 + rnd() * 3 : 1 + rnd() * 2.6;
    const o = 0.25 + rnd() * 0.55;
    particles += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(1)}" fill="${palette.accent}" opacity="${o.toFixed(2)}"/>`;
  }
  let pipMarks = '';
  for (let i = 0; i < pips; i++) {
    const x = 256 + (i - (pips - 1) / 2) * 26;
    pipMarks += `<path d="M${x} 452l8 8-8 8-8-8z" fill="${palette.accent}"/>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512">
<defs>
<radialGradient id="bg" cx="50%" cy="42%" r="78%"><stop offset="0" stop-color="${palette.light}"/><stop offset=".45" stop-color="${palette.mid}"/><stop offset="1" stop-color="${palette.dark}"/></radialGradient>
<linearGradient id="shade" x1="0" y1="0" x2="0" y2="1"><stop offset=".5" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".5"/></linearGradient>
<filter id="glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="1.1" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
</defs>
<rect width="512" height="512" fill="url(#bg)"/>
<g opacity=".9">${particles}</g>
<g fill="none" stroke="${palette.accent}" stroke-opacity=".28" stroke-width="2"><circle cx="256" cy="236" r="172"/><circle cx="256" cy="236" r="206" stroke-dasharray="2 12"/>${ticks}</g>
<g transform="translate(136 116) scale(10)" filter="url(#glow)">${parts}</g>
<rect width="512" height="512" fill="url(#shade)"/>
${pipMarks}
</svg>`;
}

export function portraitFile(name: string, glyph: GlyphName, palette: Palette, seed = 1, pips = 0): GeneratedFile {
  return { name, mime: 'image/svg+xml', bytes: enc(portraitSvg(glyph, palette, seed, pips)), width: 512, height: 512 };
}

export function paletteSheetFile(name: string, title: string, swatches: { hex: string; label: string }[]): GeneratedFile {
  const w = 960;
  const h = 540;
  const sw = (w - 120) / swatches.length;
  const cells = swatches
    .map((s, i) => {
      const x = 60 + i * sw;
      return `<rect x="${x + 6}" y="170" width="${sw - 12}" height="250" rx="18" fill="${s.hex}"/>
<text x="${x + 22}" y="458" font-family="Georgia, serif" font-size="22" fill="#E9EDF5">${s.label}</text>
<text x="${x + 22}" y="486" font-family="Menlo, Consolas, monospace" font-size="16" fill="#8B93A7">${s.hex}</text>`;
    })
    .join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
<rect width="${w}" height="${h}" fill="#0E1118"/>
<text x="60" y="92" font-family="Georgia, serif" font-size="44" fill="#F3F5FA">${title}</text>
<text x="60" y="130" font-family="Menlo, Consolas, monospace" font-size="16" letter-spacing="3" fill="#8B93A7">COLOR SCRIPT · DRAFT</text>
${cells}
</svg>`;
  return { name, mime: 'image/svg+xml', bytes: enc(svg), width: w, height: h };
}

export function heatMeterFile(name: string): GeneratedFile {
  const w = 960;
  const h = 420;
  let ticks = '';
  for (let i = 0; i <= 20; i++) {
    const x = 120 + i * 36;
    ticks += `<line x1="${x}" y1="${i % 5 === 0 ? 262 : 268}" x2="${x}" y2="278" stroke="#5B6477" stroke-width="2"/>`;
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
<defs><linearGradient id="heat" x1="0" x2="1"><stop offset="0" stop-color="#FFD166"/><stop offset=".6" stop-color="#FF7A2F"/><stop offset="1" stop-color="#E5383B"/></linearGradient>
<filter id="g"><feGaussianBlur stdDeviation="8"/></filter></defs>
<rect width="${w}" height="${h}" fill="#12151D"/>
<rect x="60" y="40" width="840" height="340" rx="28" fill="#191D28" stroke="#2A3142"/>
<text x="120" y="130" font-family="Georgia, serif" font-size="40" fill="#F3F5FA">Heat</text>
<text x="840" y="130" text-anchor="end" font-family="Menlo, Consolas, monospace" font-size="40" fill="#FF9A4D">72</text>
<rect x="120" y="170" width="720" height="72" rx="36" fill="#0C0E14" stroke="#2A3142"/>
<rect x="126" y="176" width="${(708 * 0.72).toFixed(0)}" height="60" rx="30" fill="url(#heat)"/>
<rect x="126" y="176" width="${(708 * 0.72).toFixed(0)}" height="60" rx="30" fill="url(#heat)" opacity=".55" filter="url(#g)"/>
<rect x="698" y="170" width="142" height="72" rx="0" fill="#E5383B" opacity=".12"/>
${ticks}
<text x="769" y="318" text-anchor="middle" font-family="Menlo, Consolas, monospace" font-size="16" letter-spacing="3" fill="#E5383B">OVERHEAT</text>
<text x="120" y="318" font-family="Menlo, Consolas, monospace" font-size="16" letter-spacing="3" fill="#5B6477">+1 PER HIT · −4 / S OUT OF COMBAT</text>
</svg>`;
  return { name, mime: 'image/svg+xml', bytes: enc(svg), width: w, height: h };
}

/** Normalised peaks (0..1) for waveform previews. */
export function computePeaks(samples: Float32Array, buckets = 120): number[] {
  const size = Math.max(1, Math.floor(samples.length / buckets));
  const out: number[] = [];
  for (let b = 0; b < buckets; b++) {
    let max = 0;
    for (let i = b * size; i < Math.min(samples.length, (b + 1) * size); i++) max = Math.max(max, Math.abs(samples[i]!));
    out.push(Math.round(max * 1000) / 1000);
  }
  const top = Math.max(...out, 1e-6);
  return out.map((p) => Math.round((p / top) * 1000) / 1000);
}

export function encodeWav(samples: Float32Array, sampleRate: number): Uint8Array {
  const bytes = new Uint8Array(44 + samples.length * 2);
  const view = new DataView(bytes.buffer);
  const str = (off: number, s: string) => [...s].forEach((c, i) => view.setUint8(off + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  str(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return bytes;
}

/** A whoosh followed by a boom: good enough to show off the audio preview. */
export function fireballWav(name = 'fireball-impact.wav'): GeneratedFile {
  const sr = 22050;
  const duration = 1.6;
  const n = Math.floor(sr * duration);
  const out = new Float32Array(n);
  const rnd = mulberry32(42);
  let lp = 0;
  let lp2 = 0;
  let phase = 0;
  const hit = 0.5;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const noise = rnd() * 2 - 1;
    const cutoff = 0.015 + 0.22 * Math.min(1, t / hit);
    lp += cutoff * (noise - lp);
    const whoosh = t < hit ? (t / hit) ** 2.2 : Math.exp(-(t - hit) * 22);
    let boom = 0;
    if (t >= hit) {
      const tb = t - hit;
      const f = 38 + 70 * Math.exp(-tb * 7);
      phase += (2 * Math.PI * f) / sr;
      lp2 += 0.08 * (noise - lp2);
      boom = Math.sin(phase) * Math.exp(-tb * 3.4) * 0.95 + lp2 * Math.exp(-tb * 5) * 1.6 + noise * Math.exp(-tb * 38) * 0.5;
    }
    out[i] = lp * whoosh * 2.2 + boom;
  }
  let peak = 0;
  for (const s of out) peak = Math.max(peak, Math.abs(s));
  for (let i = 0; i < n; i++) out[i] = (out[i]! / peak) * 0.9;
  return { name, mime: 'audio/wav', bytes: encodeWav(out, sr), duration, peaks: computePeaks(out) };
}
